import {
    IconButton,
    Button,
    HStack,
    Icon,
    StackDivider,
    Text,
    Tooltip,
    VStack,
    Link as ChakraLink,
    Progress,
    ButtonProps,
    Tag,
    Box,
    Wrap,
} from '@chakra-ui/react';
import { ValueViewProps } from '../../../../view';
import { ReportingViewBaseConfig } from '../../../../base';
import { assert } from '@varos/util-typescript';

export function createValueItemView(
    config: ReportingViewBaseConfig & {
        tooltip: boolean;
        compact: boolean;
    }
): React.FC<ValueViewProps> {
    const {
        UI: {
            Application: { Link },
        },
    } = config;

    function getRatingColorScheme(props: {
        value: number;
        min: number;
        max: number;
    }): ButtonProps['colorScheme'] {
        const { value, min, max } = props;
        const range = max - min;

        if (range === 0) {
            // Handle cases where min and max are the same, or max is less than min.
            // Default to green if value is at or above max, otherwise red.
            return value >= max ? 'green' : 'red';
        }

        const lowerBoundOrange = min + range / 3;
        const lowerBoundGreen = min + (2 * range) / 3;

        if (value >= lowerBoundGreen) {
            return 'green';
        }
        if (value >= lowerBoundOrange) {
            return 'orange';
        }
        return 'red';
    }

    return (props) => {
        if (props.value === null) {
            return <Text color="gray.500">N/A</Text>;
        }
        let content: React.ReactNode = null;
        if (props.value.kind === 'rating' && config.compact === false) {
            const colorScheme = getRatingColorScheme(props.value);
            content = (
                <HStack spacing={3}>
                    <Progress
                        bg="whiteAlpha.50"
                        w="full"
                        minW="12rem"
                        size="sm"
                        colorScheme={colorScheme}
                        // Add some minimum width
                        value={Math.max(props.value.value, props.value.min + 1)}
                        min={props.value.min}
                        max={props.value.max}
                    />
                    <Text
                        whiteSpace="nowrap"
                        fontWeight="bold"
                        fontSize="sm"
                        color={`${colorScheme}.200`}
                    >
                        {props.value.value}
                    </Text>
                </HStack>
            );
        }
        if (props.value.kind === 'enum') {
            content = (
                <HStack
                    bg="whiteAlpha.200"
                    borderRadius="full"
                    py={1}
                    px={2}
                    spacing={2}
                    cursor="default"
                    {...(config.compact
                        ? {
                              h: 5,
                              fontSize: 'sm',
                          }
                        : {
                              h: 6,
                              fontSize: 'md',
                          })}
                >
                    {!props.value.icon && (
                        <Box w={2} h={2} borderRadius="full" bg="whiteAlpha.700" />
                    )}
                    {props.value.icon?.kind === 'emoji' && (
                        <Text
                            {...(config.compact
                                ? {
                                      fontSize: 'sm',
                                  }
                                : {
                                      fontSize: 'md',
                                  })}
                        >
                            {props.value.icon.text}
                        </Text>
                    )}
                    <Text color="whiteAlpha.700">{props.value.label}</Text>
                </HStack>
            );
        }
        if (props.value.kind === 'rating' && config.compact === true) {
            const colorScheme = getRatingColorScheme(props.value);
            content = (
                <Tag size="md" colorScheme={colorScheme}>
                    <HStack spacing={3}>
                        <Text
                            whiteSpace="nowrap"
                            fontWeight="bold"
                            fontSize="sm"
                            color={`${colorScheme}.200`}
                        >
                            <Box as="span">{props.value.value}</Box>
                            <Box as="span"> / </Box>
                            <Box as="span">{props.value.max}</Box>
                        </Text>
                    </HStack>
                </Tag>
            );
        }
        if (props.value.kind === 'link') {
            content = (
                <HStack>
                    <ChakraLink
                        whiteSpace="nowrap"
                        href={props.value.href}
                        isExternal={true}
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        {props.value.label}
                    </ChakraLink>
                </HStack>
            );
        }
        if (props.value.kind === 'relation' && props.value.cardinality === 'one') {
            return (
                <ChakraLink
                    as={Link}
                    to={props.value.link.to}
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Tooltip
                        placement="top"
                        openDelay={300}
                        px={2}
                        py={1}
                        bg="gray.50"
                        borderRadius="md"
                        color="gray.900"
                        isDisabled={!config.tooltip}
                        shouldWrapChildren={true}
                        label={props.property.label}
                    >
                        <HStack
                            spacing={1}
                            flexBasis={0}
                            textDecor="underline"
                            color="whiteAlpha.700"
                            _hover={{ color: 'whiteAlpha.800' }}
                        >
                            {props.value.icon.kind === 'emoji' && (
                                <Text>{props.value.icon.text}</Text>
                            )}
                            {props.value.icon.kind === 'icon' && (
                                <Icon fontSize="md" as={props.value.icon.Component} />
                            )}
                            <Text noOfLines={1} maxW={config.compact ? 32 : 64}>
                                {props.value.label}
                            </Text>
                        </HStack>
                    </Tooltip>
                </ChakraLink>
            );
        }
        if (props.value.kind === 'relation' && props.value.cardinality === 'many') {
            return (
                <Wrap spacing={1} shouldWrapChildren={true}>
                    {props.value.items.map((item) => (
                        <ChakraLink
                            key={item.id}
                            as={Link}
                            to={item.link.to}
                            onClick={(event) => {
                                event.stopPropagation();
                            }}
                        >
                            <Tooltip
                                placement="top"
                                openDelay={300}
                                px={2}
                                py={1}
                                bg="gray.50"
                                borderRadius="md"
                                color="gray.900"
                                isDisabled={!config.tooltip}
                                shouldWrapChildren={true}
                                label={props.property.label}
                            >
                                <HStack
                                    bg="whiteAlpha.200"
                                    borderRadius="full"
                                    py={1}
                                    px={2}
                                    spacing={2}
                                    flexShrink={0}
                                    _hover={{ bg: 'whiteAlpha.100' }}
                                    {...(config.compact
                                        ? {
                                              h: 5,
                                              fontSize: 'sm',
                                          }
                                        : {
                                              h: 6,
                                              fontSize: 'md',
                                          })}
                                >
                                    {!item.icon && (
                                        <Box
                                            w={2}
                                            h={2}
                                            borderRadius="full"
                                            bg="whiteAlpha.700"
                                        />
                                    )}
                                    {item.icon?.kind === 'emoji' && (
                                        <Text
                                            {...(config.compact
                                                ? {
                                                      fontSize: 'sm',
                                                  }
                                                : {
                                                      fontSize: 'md',
                                                  })}
                                        >
                                            {item.icon.text}
                                        </Text>
                                    )}
                                    <Text
                                        color="whiteAlpha.700"
                                        noOfLines={1}
                                        maxW={config.compact ? 32 : 64}
                                    >
                                        {item.label}
                                    </Text>
                                </HStack>
                            </Tooltip>
                        </ChakraLink>
                    ))}
                </Wrap>
            );
        }
        if (content === null) {
            return (
                <HStack>
                    <Text>{props.value.label}</Text>;
                </HStack>
            );
        }
        return (
            <Tooltip
                {...props.tooltip}
                placement="top"
                openDelay={300}
                px={2}
                py={1}
                bg="gray.50"
                borderRadius="md"
                color="gray.900"
                isDisabled={!config.tooltip}
                shouldWrapChildren={true}
            >
                {content}
            </Tooltip>
        );
    };
}
